import React, { useEffect, useRef, useState } from "react";
import { publicGet, publicPost } from "../admin/api";
import { loadTurnstile, turnstileTheme } from "../lib/turnstile";
import PrivacyNotice from "../components/brand/PrivacyNotice";

// "Request a scan" links from the Check page arrive as /report?scan=1&server=<name or URL>.
const SCAN_REQUEST_TEXT = "Please scan this server. The HerdSafe Check page had no record of it.";

function prefill() {
  try {
    const q = new URLSearchParams(window.location.search);
    const scan = q.get("scan") === "1";
    return { scan, server: (q.get("server") || "").slice(0, 512), what_happened: scan ? SCAN_REQUEST_TEXT : "" };
  } catch {
    return { scan: false, server: "", what_happened: "" };
  }
}

export default function ReportServer() {
  const [cfg, setCfg] = useState(null);
  const [initial] = useState(prefill);
  const [form, setForm] = useState({ server: initial.server, what_happened: initial.what_happened, email: "", website: "" });
  const [token, setToken] = useState("");
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const widget = useRef(null);
  const widgetId = useRef(null);

  useEffect(() => {
    publicGet("/public/report-config").then(setCfg).catch((ex) => setErr(ex.message));
  }, []);

  useEffect(() => {
    if (!cfg || !cfg.turnstile_enabled || !cfg.turnstile_site_key) return undefined;
    let cancelled = false;
    loadTurnstile().then((ts) => {
      if (cancelled || !widget.current) return;
      widgetId.current = ts.render(widget.current, {
        sitekey: cfg.turnstile_site_key, theme: turnstileTheme(),
        callback: (t) => setToken(t), "expired-callback": () => setToken(""), "error-callback": () => setToken(""),
      });
    }).catch((ex) => setErr(ex.message));
    return () => { cancelled = true; };
  }, [cfg]);

  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      await publicPost("/public/report-suspicious", {
        server: form.server.trim(), what_happened: form.what_happened.trim(),
        email: form.email.trim() || null, website: form.website, turnstile_token: token,
      });
      setDone(true);
    } catch (ex) {
      setErr(ex.message);
      if (window.turnstile && widgetId.current != null) { window.turnstile.reset(widgetId.current); setToken(""); }
    } finally { setBusy(false); }
  };

  const limits = cfg?.limits || { server_max: 512, text_min: 10, text_max: 4000 };
  const closed = cfg && !cfg.reporting_available;
  const needsToken = !!cfg?.turnstile_enabled && !token;
  return (
    <section data-testid="report-page" className="space-y-6">
      <h1 className="hs-display text-3xl md:text-4xl">{initial.scan ? "Request a scan" : "Report a suspicious MCP server"}</h1>
      {initial.scan && (
        <p data-testid="report-scan-intro" className="hs-prose">
          We have no record of this server yet. Send it to us and it joins the scan queue; add anything you have
          noticed below.
        </p>
      )}
      <p className="text-muted hs-prose">
        Saw an MCP server behave badly, such as telling your agent to ignore its instructions, reading files it should
        not, or sending data somewhere unexpected? Tell us. A reviewer looks at every report. A report never
        changes a public verdict by itself; it only moves the server up our review queue.
      </p>
      <p className="text-muted text-sm hs-prose">
        Please do not include passwords, API keys or other secrets.
      </p>
      {done ? (
        <div data-testid="report-done" role="status" className="hs-card max-w-2xl">
          <span className="hs-badge hs-badge-safe mr-2">Received</span>
          Thank you, your report was received. A reviewer will take a look.
        </div>
      ) : closed ? (
        <div data-testid="report-closed" className="hs-card max-w-2xl">
          Reporting is temporarily unavailable. Please email <a className="hs-link" href="mailto:security@herdsafe.ai">security@herdsafe.ai</a> instead.
        </div>
      ) : (
        <form onSubmit={submit} data-testid="report-form" className="hs-card max-w-2xl space-y-4">
          <div>
            <label className="hs-label" htmlFor="report-server">Server name or URL</label>
            <input id="report-server" data-testid="report-server" required minLength={3} maxLength={limits.server_max}
                   className="hs-input hs-mono" placeholder="io.github.example/server-name, or https://…" value={form.server}
                   onChange={(e) => setForm({ ...form, server: e.target.value })} />
          </div>
          <div>
            <label className="hs-label" htmlFor="report-text">What happened?</label>
            <textarea id="report-text" data-testid="report-text" required minLength={limits.text_min} maxLength={limits.text_max}
                      rows={6} className="hs-input" aria-describedby="report-text-count"
                      value={form.what_happened} onChange={(e) => setForm({ ...form, what_happened: e.target.value })} />
            <span id="report-text-count" className="hs-hint">{form.what_happened.length}/{limits.text_max}</span>
          </div>
          <div>
            <label className="hs-label" htmlFor="report-email">Your email (optional, for a confirmation and follow-up questions)</label>
            <input id="report-email" data-testid="report-email" type="email" maxLength={254} className="hs-input" value={form.email}
                   onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </div>
          {/* Honeypot: invisible to people, tempting to bots. */}
          <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", height: 0, overflow: "hidden" }}>
            <label>Website<input tabIndex={-1} autoComplete="off" data-testid="report-hp" value={form.website}
                                 onChange={(e) => setForm({ ...form, website: e.target.value })} /></label>
          </div>
          {cfg?.turnstile_enabled && <div ref={widget} data-testid="report-turnstile" />}
          {err && <div data-testid="report-error" role="alert" className="hs-text-danger">{err}</div>}
          <button data-testid="report-submit" type="submit" disabled={busy || !cfg || needsToken} className="hs-btn hs-btn-primary">
            {busy ? "Sending…" : "Send report"}
          </button>
          <PrivacyNotice />
        </form>
      )}
    </section>
  );
}
